import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import type { ApiError } from "@zaphub/types";

import { useSessao } from "./sessao";

export const API_URL: string = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

/** Erro normalizado: o front ramifica por `code`, nunca por `message`. */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    mensagem: string,
    readonly fields?: Record<string, string[]>,
  ) {
    super(mensagem);
    this.name = "ErroApi";
  }
}

export const api = axios.create({ baseURL: API_URL, withCredentials: true, timeout: 30_000 });

api.interceptors.request.use((config) => {
  const token = useSessao.getState().accessToken;
  if (token && !config.headers.has("Authorization")) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

/** Quem navega (router) registra aqui para o interceptor poder redirecionar. */
let aoPerderSessao: () => void = () => undefined;
let aoAssinaturaInativa: () => void = () => undefined;
export function registrarRedirecionadores(r: { perderSessao: () => void; assinaturaInativa: () => void }): void {
  aoPerderSessao = r.perderSessao;
  aoAssinaturaInativa = r.assinaturaInativa;
}

/** Refresh single-flight: várias requisições 401 simultâneas disparam UM refresh. */
let refreshEmAndamento: Promise<string | null> | null = null;

export function renovarToken(): Promise<string | null> {
  refreshEmAndamento ??= axios
    .post<{ accessToken: string }>(`${API_URL}/auth/refresh`, null, { withCredentials: true, timeout: 15_000 })
    .then((r) => {
      useSessao.getState().definirToken(r.data.accessToken);
      return r.data.accessToken;
    })
    .catch(() => null)
    .finally(() => {
      refreshEmAndamento = null;
    });
  return refreshEmAndamento;
}

type ConfigComRetry = AxiosRequestConfig & { _retentado?: boolean };

api.interceptors.response.use(
  (r) => r,
  async (erro: AxiosError<ApiError>) => {
    const original = erro.config as ConfigComRetry | undefined;
    const corpo = erro.response?.data;
    const status = erro.response?.status ?? 0;
    const ehAuth = original?.url?.startsWith("/auth/");

    if (status === 401 && original && !original._retentado && !ehAuth && useSessao.getState().accessToken) {
      original._retentado = true;
      const novo = await renovarToken();
      if (novo) {
        original.headers = { ...original.headers, Authorization: `Bearer ${novo}` };
        return api(original);
      }
      useSessao.getState().limpar();
      aoPerderSessao();
    }
    if (status === 402 && corpo?.code === "assinatura_inativa") aoAssinaturaInativa();

    throw new ErroApi(
      status,
      corpo?.code ?? (erro.code === "ECONNABORTED" ? "timeout" : "rede"),
      corpo?.message ??
        (status === 0 ? "Não foi possível falar com o servidor. Verifique sua conexão." : "Erro inesperado."),
      corpo?.fields,
    );
  },
);

/** Mensagem pronta para toast/tela a partir de qualquer erro. */
export function mensagemDeErro(erro: unknown): string {
  return erro instanceof ErroApi ? erro.message : "Algo deu errado. Tente novamente.";
}
